import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Result,
  toBase, fromBase, altsFor, fmt, UNITS,
  SpecCard, SpecValue,
} from "./_shared";

/**
 *  RO RECOVERY & BRINE FLOW
 *  --------------------------------------------------------------
 *  Permeate:   Q_p = Q_f × R
 *  Reject:     Q_r = Q_f − Q_p
 *  Conc. factor CF = 1 / (1 − R)
 *  Reject TDS ≈ Feed TDS × CF  (100 % rejection — conservative)
 *  Specific energy ≈ 3.0–4.5 kWh / m³ for brackish RO at 75 % R.
 *  --------------------------------------------------------------
 */
export default function ROCalculator() {
  const [feedVal, setFeedVal] = useState(10);
  const [feedUnit, setFeedUnit] = useState("m3h");
  const [recoveryPct, setRecoveryPct] = useState(75);
  const [feedTds, setFeedTds] = useState(800);          // mg/L (typical SA borehole)
  const [hoursPerDay, setHoursPerDay] = useState(20);

  const R = (recoveryPct || 0) / 100;
  const Qf_m3s = toBase("flow", feedVal, feedUnit);
  const Qp_m3s = Qf_m3s * R;
  const Qr_m3s = Qf_m3s - Qp_m3s;
  const CF     = R < 1 ? 1 / (1 - R) : null;
  const rejectTds = CF !== null ? feedTds * CF : null;

  const dailyP_L = Qp_m3s * hoursPerDay * 3600 * 1000;   // m³/s × s/h × h × 1000 L/m³
  const dailyR_L = Qr_m3s * hoursPerDay * 3600 * 1000;

  // Recovery status — scaling limits for typical brackish RO membranes
  const recStatus = recoveryPct >= 85 ? "alert"
                  : recoveryPct >= 80 ? "warn"
                  : recoveryPct >= 50 ? "ok"
                  : "warn";
  const recNote = recoveryPct >= 85
    ? "Above 85 % — severe scaling risk on brackish RO. Multi-pass or interstage boost required."
    : recoveryPct >= 80
    ? "Above 80 % — scaling risk for hard / brackish feeds. Check LSI on the reject stream."
    : recoveryPct >= 50
    ? "Within the typical brackish-RO range (50–80 %)."
    : "Below 50 % — feasible but operationally inefficient; check pump and array sizing.";

  return (
    <ToolShell
      slug="ro-recovery"
      title="RO recovery & brine flow"
      oneLiner="Permeate / reject split for a reverse-osmosis system at a given recovery, plus reject concentration factor."
      onExport={() => ({
        inputs: [
          { label: "Feed flow", value: feedVal, unit: UNITS.flow.labels[feedUnit] },
          { label: "Target recovery", value: recoveryPct, unit: "%" },
          { label: "Feed TDS", value: feedTds, unit: "mg/L" },
          { label: "Operating hours per day", value: hoursPerDay, unit: "h/day" },
        ],
        results: [
          { label: "Permeate flow", value: fmt(Qp_m3s * 3600), unit: "m³/h",
            alts: altsFor("flow", Qp_m3s, ["Lps", "Lpm", "gpm"], 1) },
          { label: "Reject (brine) flow", value: fmt(Qr_m3s * 3600), unit: "m³/h",
            alts: altsFor("flow", Qr_m3s, ["Lps", "Lpm", "gpm"], 1) },
          { label: "Concentration factor", value: CF !== null ? fmt(CF) : "—", unit: "×",
            status: recStatus, note: recNote },
          { label: "Reject TDS (estimated)", value: fmt(rejectTds, 0), unit: "mg/L",
            note: "Assuming 100 % salt rejection — conservative." },
          { label: "Daily permeate volume", value: fmt(dailyP_L, 0), unit: "L / day",
            alts: dailyP_L !== null ? [{ value: fmt(dailyP_L / 1000, 1), unit: "kL" }] : null },
          { label: "Daily reject volume", value: fmt(dailyR_L, 0), unit: "L / day",
            alts: dailyR_L !== null ? [{ value: fmt(dailyR_L / 1000, 1), unit: "kL" }] : null },
        ],
        notes: [
          "Recovery R = Permeate / Feed.  Concentration factor CF = 1 / (1 − R).",
          "Reject TDS estimated at Feed TDS × CF assuming 100 % salt rejection.",
        ],
        spec: {
          title: "RO recovery specification",
          body: `At <strong>${fmt(feedVal, 2)} ${UNITS.flow.labels[feedUnit]}</strong> feed and <strong>${recoveryPct} %</strong> recovery, the system produces <strong>${fmt(Qp_m3s * 3600, 2)} m³/h</strong> permeate and <strong>${fmt(Qr_m3s * 3600, 2)} m³/h</strong> reject. Running <strong>${hoursPerDay} h/day</strong> delivers <strong>${fmt(dailyP_L / 1000, 1)} kL/day</strong> of treated water. Reject TDS rises to <strong>≈ ${fmt(rejectTds, 0)} mg/L</strong> (CF = ${CF !== null ? fmt(CF, 2) : "—"}×).`,
        },
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Feed flow">
          <UnitNumber dim="flow" value={feedVal} onChange={setFeedVal} unit={feedUnit} onUnitChange={setFeedUnit}
            units={["Lps", "Lpm", "m3h", "m3d", "gpm"]} testid="ro-feed" />
        </Field>
        <Field label="Target recovery" hint="Typical 50–80 % for brackish RO · 35–45 % for seawater.">
          <PercentInput value={recoveryPct} onChange={setRecoveryPct} testid="ro-recovery" />
        </Field>
        <Field label="Feed TDS" hint="From a water analysis — drives the reject TDS estimate.">
          <SuffixNum value={feedTds} onChange={setFeedTds} suffix="mg/L" testid="ro-tds" />
        </Field>
        <Field label="Operating hours per day">
          <SuffixNum value={hoursPerDay} onChange={setHoursPerDay} suffix="h/day" testid="ro-hours" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Result label="Permeate flow"
          primary={{ value: fmt(Qp_m3s * 3600), unit: "m³/h" }}
          alts={altsFor("flow", Qp_m3s, ["Lps", "Lpm", "gpm"], 1)}
          testid="ro-result-permeate" />
        <Result label="Reject (brine) flow"
          primary={{ value: fmt(Qr_m3s * 3600), unit: "m³/h" }}
          alts={altsFor("flow", Qr_m3s, ["Lps", "Lpm", "gpm"], 1)}
          testid="ro-result-reject" />
        <Result label="Concentration factor"
          primary={{ value: CF !== null ? fmt(CF) : "—", unit: "×" }}
          status={recStatus} note={recNote}
          testid="ro-result-cf" />
        <Result label="Reject TDS (estimated)"
          primary={{ value: fmt(rejectTds, 0), unit: "mg/L" }}
          note="Assuming 100 % salt rejection — conservative."
          testid="ro-result-reject-tds" />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Result label="Daily permeate volume"
          primary={{ value: fmt(dailyP_L, 0), unit: "L / day" }}
          alts={dailyP_L !== null ? [{ value: fmt(dailyP_L / 1000, 1), unit: "kL" }] : null}
          testid="ro-result-daily-p" />
        <Result label="Daily reject volume"
          primary={{ value: fmt(dailyR_L, 0), unit: "L / day" }}
          alts={dailyR_L !== null ? [{ value: fmt(dailyR_L / 1000, 1), unit: "kL" }] : null}
          testid="ro-result-daily-r" />
      </div>

      <SpecCard
        eyebrow="RO recovery specification"
        testid="ro-spec-card"
        footer={recStatus === "alert" || recStatus === "warn" ? recNote : null}
      >
        At <SpecValue>{fmt(feedVal, 2)} {UNITS.flow.labels[feedUnit]}</SpecValue> feed and{" "}
        <SpecValue>{recoveryPct} %</SpecValue> recovery, the system produces{" "}
        <SpecValue>{fmt(Qp_m3s * 3600, 2)} m³/h</SpecValue> permeate and{" "}
        <SpecValue>{fmt(Qr_m3s * 3600, 2)} m³/h</SpecValue> reject. Running{" "}
        <SpecValue>{hoursPerDay} h/day</SpecValue> delivers{" "}
        <SpecValue>{fmt(dailyP_L / 1000, 1)} kL/day</SpecValue> of treated water. Reject TDS rises to{" "}
        <SpecValue>≈ {fmt(rejectTds, 0)} mg/L</SpecValue> (CF = {CF !== null ? fmt(CF, 2) : "—"}×).
      </SpecCard>
    </ToolShell>
  );
}

function PercentInput({ value, onChange, testid }) {
  // Decimal-safe string buffer (see _shared.SuffixNum).
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input type="text" inputMode="decimal" value={buf} placeholder="0"
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
        style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.95rem", fontWeight: 500 }}
        data-testid={testid} />
      <span className="inline-flex items-center px-3 border border-l-0 font-mono"
        style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}>%</span>
    </div>
  );
}
function SuffixNum({ value, onChange, suffix, testid }) {
  // Buffered text-mode input — see SuffixNum (PurificationPlantSizing.jsx).
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input type="text" inputMode="decimal" value={buf} placeholder="0"
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
        style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.95rem", fontWeight: 500 }}
        data-testid={testid} />
      <span className="inline-flex items-center px-3 border border-l-0 font-mono"
        style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.65)", fontSize: "0.72rem", letterSpacing: "0.04em" }}>{suffix}</span>
    </div>
  );
}
